import { Box } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { Redirect, Route, Switch } from "react-router-dom";
import useErrorBoundary from "use-error-boundary";

import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";

import ActionPlansPage from "../pages/action-plans/ActionPlansPage";
import ChannelSpendPage from "../pages/channel-spend-inputs/ChannelSpendPage";
import { ScenarioBuilderForm } from "../pages/scenario-form/ScenarioFormPage";
import { ScenarioFormProvider } from "../pages/scenario-form/ScenarioFormProvider";
import { ScenarioInputsPage } from "../pages/scenario-results/ScenarioInputsPage";
import { ScenarioLikelihoodPage } from "../pages/scenario-results/ScenarioLikelihoodPage";
import { ScenarioResultsLayout } from "../pages/scenario-results/ScenarioResultsLayout";
import { ScenarioResultsProvider } from "../pages/scenario-results/ScenarioResultsProvider";
import { ScenariosResultsPage } from "../pages/scenario-results/ScenariosResultsPage";
import { ScenariosPage } from "../pages/scenarios/ScenariosPage";
import { ActionPlanResultsLayout } from "../pages/action-plan-results/ActionPlanResultsLayout";
import { ActionPlanResultsPage } from "../pages/action-plan-results/ActionPlanResultsPage";
import { ActionPlanResultsProvider } from "../pages/action-plan-results/ActionPlanResultsProvider";

export const ScenarioV3Router = () => {
  const { value: isActionPlansEnabled } = useFeatureGate(featureFlag.scenarioBuilderActionPlans);
  const { value: isRiskEnabled } = useFeatureGate(featureFlag.scenarioBuilderRisk);

  const { ErrorBoundary, didCatch } = useErrorBoundary({
    onDidCatch(error, errorInfo) {
      console.error(error, errorInfo);
    },
  });

  if (didCatch) {
    return <></>;
  }

  return (
    <ErrorBoundary>
      <Box sx={{ flexGrow: 1, overflow: "auto" }}>
        <Switch>
          <Route exact path="/scenarios-v3">
            <Redirect to="/scenarios-v3/all" />
          </Route>
          {isActionPlansEnabled && (
            <Route exact path="/scenarios-v3/action-plans" component={ActionPlansPage} />
          )}
          {isActionPlansEnabled && (
            <Route
              path="/scenarios-v3/action-plans/:uuid"
              render={({ match }) => (
                <ActionPlanResultsProvider>
                  <ActionPlanResultsLayout>
                    <Switch>
                      <Route path={`${match.path}`} component={ActionPlanResultsPage} />
                    </Switch>
                  </ActionPlanResultsLayout>
                </ActionPlanResultsProvider>
              )}
            />
          )}
          <Route exact path="/scenarios-v3/all" component={ScenariosPage} />
          <Route
            path="/scenarios-v3/:uuid/form"
            render={({ match }) => (
              <ScenarioFormProvider>
                <Switch>
                  <Route exact path={match.path} component={ScenarioBuilderForm} />
                  <Route
                    exact
                    path={`${match.path}/channel-spend-inputs`}
                    component={ChannelSpendPage}
                  />
                </Switch>
              </ScenarioFormProvider>
            )}
          />
          <Route
            path="/scenarios-v3/:uuid"
            render={({ match }) => (
              <ScenarioResultsProvider>
                <ScenarioResultsLayout>
                  <Switch>
                    <Route path={`${match.path}/results`} component={ScenariosResultsPage} />
                    <Route path={`${match.path}/inputs`} component={ScenarioInputsPage} />
                    {isRiskEnabled && (
                      <Route path={`${match.path}/likelihood`} component={ScenarioLikelihoodPage} />
                    )}
                    <Route exact path={match.path}>
                      <Redirect to={`${match.path}/results`} />
                    </Route>
                  </Switch>
                </ScenarioResultsLayout>
              </ScenarioResultsProvider>
            )}
          />
        </Switch>
      </Box>
    </ErrorBoundary>
  );
};
